import { ComponentProps, MouseEvent, ReactElement } from 'react';

import { useNotify } from '@changegps/horizon-component-library';
import { useQueryClient } from '@tanstack/react-query';

import { ConfirmationModal } from '@/components/modals';

import { usersQueryKeys } from '../data';
import { useDeleteUser } from '../hooks';
import { User } from '../types';

type DeleteUserDialogProps = Required<
  Pick<ComponentProps<typeof ConfirmationModal>, 'open' | 'onClose'>
> & { uuid: User['uuid'] };

export const DeleteUserDialog = (
  props: DeleteUserDialogProps,
): ReactElement => {
  const queryClient = useQueryClient();
  const { notify } = useNotify();
  const { mutate: deleteUser, isPending } = useDeleteUser(async () => {
    props.onClose({} as MouseEvent<HTMLButtonElement>, 'backdropClick');
    await queryClient.invalidateQueries({
      queryKey: [...usersQueryKeys.getUsers],
    });
  });

  const handleClose = (event: MouseEvent<HTMLButtonElement>) => {
    if (isPending) return;
    props.onClose(event, 'backdropClick');
  };

  const handleAccept = (event: MouseEvent<HTMLButtonElement>) => {
    deleteUser(props.uuid, {
      onSuccess: () => {
        notify({
          message: 'User deleted.',
        });
        handleClose(event);
      },
      onError: () => {
        notify({
          message: 'Error deleting user!',
          severity: 'error',
        });
      },
    });
  };

  return (
    <ConfirmationModal
      open={props.open}
      isPending={isPending}
      onClose={handleClose}
      title="Delete User?"
      content="Are you sure you want to delete this user?"
      cancelActionTitle="Cancel"
      confirmActionTitle="Yes"
      onConfirm={handleAccept}
      onCancel={handleClose}
    />
  );
};
